import { colors, icons } from '@beelday/common'
import styled from '@emotion/styled'
import { FC, ReactNode } from 'react'
import { LobbySize, SCALE } from './lobby-size'

const List = styled.ol`
	margin: 0;
	padding: 0;
	width: 100%;
	list-style: none;
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const Step = styled.li`
	display: flex;
	align-items: flex-start;
	gap: 16px;
`

const Marker = styled.span<{ $size: LobbySize; $done: boolean }>`
	flex: none;
	box-sizing: border-box;
	width: ${props => SCALE[props.$size].badge}px;
	height: ${props => SCALE[props.$size].badge}px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	background: ${props =>
		props.$done ? colors.greenishCyan : colors.indigoBlue10};
	color: ${props => (props.$done ? colors.blackish : colors.indigoBlue)};
	font-size: ${props => SCALE[props.$size].body}px;
	font-weight: 700;

	svg {
		width: 55%;
		height: 55%;
	}
`

const Text = styled.div`
	min-width: 0;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 4px;
	padding-top: 2px;
`

const Title = styled.div<{ $size: LobbySize }>`
	color: ${colors.blackish};
	font-size: ${props => SCALE[props.$size].body}px;
	font-weight: 700;
	line-height: 1.3;
`

const Description = styled.div<{ $size: LobbySize }>`
	color: ${colors.brightBlackish};
	font-size: ${props => SCALE[props.$size].small}px;
	line-height: 1.4;
`

const Extra = styled.div`
	margin-top: 8px;
`

/** For a screen reader only: the marker's tick is a picture. */
const Hidden = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	overflow: hidden;
	clip: rect(0 0 0 0);
	white-space: nowrap;
	border: 0;
`

export type StepItem = {
	id: string
	title: ReactNode
	description: ReactNode
	done: boolean
	/** Under the description: who has arrived, the Present button. */
	extra?: ReactNode
}

type Props = {
	size: LobbySize
	/** Names the list for a screen reader. */
	label: string
	/** Said after a finished step's title - "Done". */
	doneLabel: ReactNode
	steps: StepItem[]
}

/**
 * The trainer's checklist: an ordered list, one step per row - a numbered
 * marker that turns into a tick when the step is done, a title and a line
 * under it. The number is the list's own, so the marker is for the eye only.
 */
export const LobbySteps: FC<Props> = ({ size, label, doneLabel, steps }) => (
	<List aria-label={label}>
		{steps.map((step, index) => (
			<Step key={step.id} data-testid={`in_person.lobby.step.${step.id}`}>
				<Marker $size={size} $done={step.done} aria-hidden="true">
					{step.done ? <icons.CheckIcon /> : index + 1}
				</Marker>
				<Text>
					<Title $size={size}>
						{step.title}
						{step.done ? <Hidden> ({doneLabel})</Hidden> : null}
					</Title>
					<Description $size={size}>{step.description}</Description>
					{step.extra ? <Extra>{step.extra}</Extra> : null}
				</Text>
			</Step>
		))}
	</List>
)
